import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Send, FileText, Info } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface Announcement {
  id: string;
  title: string;
  content: string;
  priority: string;
  category: string;
  created_at: string;
  file_url?: string | null;
}

interface AnimatedAnnouncementFeedProps {
  announcements: Announcement[];
  title?: string;
  emptyMessage?: string;
  isLoading?: boolean;
}

const priorityConfig: Record<string, { border: string; bg: string; badge: "destructive" | "default" | "secondary" | "outline"; badgeBg: string }> = {
  urgent: {
    border: "border-l-4 border-l-destructive",
    bg: "bg-destructive/5",
    badge: "destructive",
    badgeBg: "",
  },
  high: {
    border: "border-l-4 border-l-[hsl(30,80%,55%)]",
    bg: "bg-[hsl(30,80%,55%)]/5",
    badge: "default",
    badgeBg: "bg-[hsl(30,80%,55%)] hover:bg-[hsl(30,80%,55%)]",
  },
  normal: {
    border: "border-l-4 border-l-primary",
    bg: "bg-primary/5",
    badge: "secondary",
    badgeBg: "",
  },
  low: {
    border: "border-l-4 border-l-muted-foreground/30",
    bg: "bg-muted/30",
    badge: "outline",
    badgeBg: "",
  },
};

function AnnouncementItem({ announcement, index }: { announcement: Announcement; index: number }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const config = priorityConfig[announcement.priority] ?? priorityConfig.normal;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border transition-all duration-500 overflow-hidden",
        config.border,
        config.bg,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        "shadow-sm"
      )}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={config.badge} className={cn("text-[10px] px-1.5 py-0 shrink-0", config.badgeBg)}>
                {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)}
              </Badge>
              <h4 className="text-sm font-semibold">{announcement.title}</h4>
            </div>
            <p className="text-xs text-muted-foreground">
              {announcement.content}
            </p>
            {announcement.file_url && (
              <a
                href={announcement.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline mt-1"
              >
                <FileText className="h-3.5 w-3.5" /> View attachment
              </a>
            )}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0 pt-0.5">
            {format(new Date(announcement.created_at), "dd MMM yyyy")}
          </span>
        </div>
      </div>
    </div>
  );
}
export function AnimatedAnnouncementFeed({
  announcements,
  title = "Announcements & Notices",
  emptyMessage = "No announcements at this time.",
  isLoading = false,
}: AnimatedAnnouncementFeedProps) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Send className="h-4 w-4 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="animate-pulse rounded-lg border border-l-4 border-l-muted p-3 space-y-2"
              >
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-full" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : announcements.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            <Info className="h-7 w-7 mx-auto mb-2 text-muted-foreground/40" />
            {emptyMessage}
          </div>
        ) : (
          <div className="space-y-2.5">
            {announcements.map((a, i) => (
              <AnnouncementItem key={a.id} announcement={a} index={i} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
